import {
  BerthSide,
  CargoType,
  CarrierVisitScheduleDto,
  CompanyResponseDto,
  CreateVesselVisitCommand,
  UpdateVesselVisitCommand,
  VesselDto,
} from '@planning/app/api'
import { ITripId } from '@planning/components/visit/VisitTripIdsFields'
import { VesselVisitFormStoreType } from '@planning/pages/VesselVisit/Stores/VesselVisitFormStore'
import { vesselVisitService } from '@planning/services'
import { getDateTimeUtcString } from '@planning/utils'
import dayjs, { Dayjs } from 'dayjs'
import { action, computed, makeObservable, observable } from 'mobx'
import { VisitPlanningStore } from '../visit/VisitPlanningStore'
import { IVesselVisitItem } from './VesselVisitItem'

export interface IVesselVisitPlanningFormData {
  id?: number
  eta?: Dayjs
  etb?: Dayjs
  etd?: Dayjs
  vesselIds: number[]
  importDraft: number
  exportDraft: number
  dischargeEstimate: number
  loadEstimate: number
  cargoType: CargoType
  qmmFrom: number
  qmmTo: number
  berthSide: BerthSide
  tripIds: ITripId[]
  vesselServiceId?: number | null
  portRotation?: Array<string> | null
}

export class VesselVisitPlanningStore extends VisitPlanningStore {
  vesselVisit?: IVesselVisitItem
  vessels: VesselDto[] = []
  companies: CompanyResponseDto[] = []

  constructor() {
    super()
    makeObservable(this, {
      vesselVisit: observable,
      vessels: observable,
      companies: observable,

      setVesselVisit: action,
      setVessels: action,
      setCompanies: action,
      createVesselVisit: action,
      reset: action,

      companyById: computed,
    })
  }

  createVesselVisit = async (data: IVesselVisitPlanningFormData, pierId: string) => {
    const createCmd: CreateVesselVisitCommand = {
      vesselIds: data.vesselIds,
      eta: dayjs(data.eta) as any,
      etb: dayjs(data.etb) as any,
      etd: dayjs(data.etd) as any,
      inboundTripIds: this.getInboundsFromTripIds(data.tripIds),
      outboundTripIds: this.getOutboundsFromTripIds(data.tripIds),
      importDraft: data.importDraft ? parseFloat(data.importDraft.toString()) : 0.0,
      exportDraft: data.exportDraft ? parseFloat(data.exportDraft.toString()) : 0.0,
      loadEstimate: data.loadEstimate ? parseInt(data.loadEstimate.toString()) : 0,
      dischargeEstimate: data.dischargeEstimate ? parseInt(data.dischargeEstimate.toString()) : 0,
      cargoType: data.cargoType,
      schedule: this.scheduleTiming(),
      berthSide: data.berthSide,
      qmmFrom: data.qmmFrom,
      qmmTo: data.qmmTo,
      vesselServiceId: data?.vesselServiceId ?? null,
      portRotation: data?.portRotation ?? [],
      pierId: pierId,
    }

    return await vesselVisitService.post(createCmd)
  }

  updateVesselVisit = async (data: IVesselVisitPlanningFormData, pierId: string) => {
    if (!data.id) return

    const updateCmd: UpdateVesselVisitCommand = {
      id: data.id,
      vesselIds: data.vesselIds,
      eta: dayjs(data.eta) as any,
      etb: dayjs(data.etb) as any,
      etd: dayjs(data.etd) as any,
      inboundTripIds: this.getInboundsFromTripIds(data.tripIds),
      outboundTripIds: this.getOutboundsFromTripIds(data.tripIds),
      importDraft: data.importDraft ? parseFloat(data.importDraft.toString()) : 0.0,
      exportDraft: data.exportDraft ? parseFloat(data.exportDraft.toString()) : 0.0,
      loadEstimate: data.loadEstimate ? parseInt(data.loadEstimate.toString()) : 0,
      dischargeEstimate: data.dischargeEstimate ? parseInt(data.dischargeEstimate.toString()) : 0,
      cargoType: data.cargoType,
      berthSide: data.berthSide,
      qmmFrom: data.qmmFrom,
      qmmTo: data.qmmTo,
      vesselServiceId: data?.vesselServiceId ?? null,
      portRotation: data?.portRotation ?? [],
      pierId: pierId,
    }

    return await vesselVisitService.put(updateCmd)
  }

  setVesselVisit = (vesselVisit?: IVesselVisitItem) => {
    this.vesselVisit = vesselVisit
  }

  setVessels = (vessel: VesselDto[]) => {
    this.vessels = vessel
  }

  setCompanies = (companies: CompanyResponseDto[]) => {
    this.companies = companies
  }

  createVesselVisitFromFormStore = async (
    form: VesselVisitFormStoreType,
    fallbackPierId?: string,
  ) => {
    const createCmd = this.mapToCreateVesselVisitCommandFromFormStore(form, fallbackPierId)

    return await vesselVisitService.post(createCmd)
  }

  saveVesselVisitFromFormStore = async (
    id: number,
    form: VesselVisitFormStoreType,
    fallbackPierId?: string,
  ) => {
    const cmd: UpdateVesselVisitCommand = {
      ...this.mapToCreateVesselVisitCommandFromFormStore(form, fallbackPierId),
      id: id,
      voyageNumbers: form.generalInformation?.voyages?.map(v => ({
        id: v.id,
        inboundVoyageNumber: v.inboundNumber,
        outboundVoyageNumber: v.outboundNumber,
        shippingLineId: v.shippingLine?.id,
      })),
    }

    return await vesselVisitService.put(cmd)
  }

  private mapToCreateVesselVisitCommandFromFormStore = (
    form: VesselVisitFormStoreType,
    fallbackPierId?: string,
  ): CreateVesselVisitCommand => {
    const generalInfo = form.generalInformation
    const cargo = form.cargo
    const berthAndTiming = form.berthAndTiming

    if (!berthAndTiming?.pier?.id && !fallbackPierId) {
      throw new Error('Pier is required to create or update a vessel visit.')
    }

    const pierId = berthAndTiming?.pier?.id ?? fallbackPierId

    const etaUtc = getDateTimeUtcString(berthAndTiming?.etaDate, berthAndTiming?.etaTime)
    const etbUtc = getDateTimeUtcString(berthAndTiming?.etbDate, berthAndTiming?.etbTime)
    const etdUtc = getDateTimeUtcString(berthAndTiming?.etdDate, berthAndTiming?.etdTime)

    const schedule: CarrierVisitScheduleDto | undefined =
      berthAndTiming?.visitType === 'Repeating visit' && berthAndTiming.schedule
        ? {
            repeatEvery: berthAndTiming.schedule.repeatEvery,
            repeatBy: berthAndTiming.schedule.repeatBy,
            ...(berthAndTiming.schedule.endsOn &&
              berthAndTiming.schedule.selectedRadioOption === 'on' && {
                endsOn: berthAndTiming.schedule.endsOn.toISOString(),
              }),
            ...(berthAndTiming.schedule.endsAfter &&
              berthAndTiming.schedule.selectedRadioOption === 'after' && {
                endsAfter: berthAndTiming.schedule.endsAfter,
              }),
          }
        : undefined

    return {
      // general information
      vesselIds: generalInfo?.vesselIds ?? [],
      shippingAgentId: generalInfo?.shippingAgent?.id,
      vesselOperatorId: generalInfo?.vesselOperator?.id,
      portCallId: generalInfo?.portCallId,
      vesselServiceId: generalInfo?.vesselServiceId,
      portRotation: generalInfo?.portRotation,
      voyageNumbers: generalInfo?.voyages?.map(v => ({
        inboundVoyageNumber: v.inboundNumber,
        outboundVoyageNumber: v.outboundNumber,
        shippingLineId: v.shippingLine?.id,
      })),
      // cargo
      cargoType: cargo?.cargoType ?? CargoType.Container,
      dischargeEstimate: cargo?.dischargeEstimate,
      loadEstimate: cargo?.loadEstimate,
      restowEstimate: cargo?.restowEstimate,
      // berth and timing
      eta: etaUtc,
      etb: etbUtc,
      etd: etdUtc,
      schedule: schedule,
      importDraft: berthAndTiming?.draftArrival,
      exportDraft: berthAndTiming?.draftDeparture,
      pierId,
      berthSide: berthAndTiming?.dockingSide,
      qmmFrom: berthAndTiming?.qmmFrom,
      qmmTo: berthAndTiming?.qmmTo,
      // notes
      notes: form.notes,
    }
  }

  reset = () => {
    this.setVesselVisit()
    this.setVessels([])
    this.setCompanies([])

    this.resetVisit()
  }

  get companyById() {
    return this.companies.reduce(
      (acc, company) => {
        acc[company.id] = company
        return acc
      },
      {} as Record<number, CompanyResponseDto>,
    )
  }
}
